import {createContext, useContext} from 'react';

import {
  ClientResponse,
  Conversation,
  ConversationContext,
  initObservability,
  RequestAction,
} from '@stripe-internal/conversations-client';
import {
  InlineDataEventBus,
  InlineDataService,
} from '@stripe-internal/inline-data';

import {Api} from '../lib/api/api';
import {App, AppConfig, AppProfile, AppState, AppStateStore} from '../types';
import {NoMessageBus} from '../lib/parentMessageBus/ParentMessageBus';
import {SupportConversationsClient} from '../lib/SupportConversationsClient';

const noObservability = initObservability({});
const uninitializedError = (methodCalled: string) =>
  new Error(`${methodCalled}, but Assistant App not initialized`);

const Uninitialized: App = {
  get stateStore(): AppStateStore {
    throw uninitializedError('stateStore');
  },
  get state(): AppState {
    throw uninitializedError('state');
  },
  get api(): Api {
    throw uninitializedError('api');
  },
  get inlineDataEventBus(): InlineDataEventBus {
    throw uninitializedError('inlineDataEventBus');
  },
  get config(): AppConfig {
    throw uninitializedError('config');
  },
  get profile(): AppProfile {
    throw uninitializedError('profile');
  },
  get client(): SupportConversationsClient {
    throw uninitializedError('client');
  },
  get inlineDataService(): InlineDataService {
    throw uninitializedError('inlineDataService');
  },
  hasFlag(_: string): boolean {
    return false;
  },
  requestAction(_: Omit<RequestAction, 'name'>): Promise<ClientResponse> {
    throw uninitializedError('requestAction');
  },
  getConversation(_: string): Conversation | undefined {
    throw uninitializedError('getConversation');
  },
  startConversation(_: {
    conversationId?: string;
    initialQuery?: string;
    restart?: boolean;
    context?: Partial<ConversationContext>;
  }): Promise<Conversation | null> {
    throw uninitializedError('startConversation');
  },
  restartConversation(_: {
    initialQuery?: string;
  }): Promise<Conversation | null> {
    throw uninitializedError('restartConversation');
  },
  endConversation(): Promise<void> {
    throw uninitializedError('endConversation');
  },
  resumeOrStartConversation(_: {
    conversationId?: string;
    initialQuery?: string;
    restart?: boolean;
    context?: Partial<ConversationContext>;
  }): Promise<Conversation | null> {
    throw uninitializedError('resumeOrStartConversation');
  },
  open(): void {
    throw uninitializedError('open');
  },
  close(): void {
    throw uninitializedError('close');
  },
  minimize() {
    throw uninitializedError('minimize');
  },
  init() {
    throw uninitializedError('init');
  },
  updateConfig() {
    throw uninitializedError('updateConfig');
  },
  updateProfile() {
    throw uninitializedError('updateProfile');
  },
  updateOnClose() {
    throw uninitializedError('updateOnClose');
  },
  updateParentMessageBus() {
    throw uninitializedError('updateParentMessageBus');
  },
  destroy() {
    throw uninitializedError('destroy');
  },
  parentMessageBus: NoMessageBus,
  errors: noObservability.errors,
  analytics: noObservability.analytics,
  metrics: noObservability.metrics,
};

export const AppContext = createContext<App>(Uninitialized);

export function useApp(): App {
  return useContext(AppContext);
}
